// TBD

import { descriptors } from "./descriptors.js";
import { coreEngine } from "./engine.js";
import { nodes } from "./nodes.js";
import { createSingletonLogger } from "./singleton-logger.js";

class RuntimeComponentRegistry {
  static componentName = "runtime-component-registry";

  #runtimeComponents = new Map(); // namespace -> component class
  #componentInstances = new Map(); // namespace -> instance
  #componentDescriptors = new Map(); // namespace -> descriptor
  #componentDependencies = new Map(); // namespace -> Set of dependent namespaces
  #logger;

  constructor() {
    this.#logger = createSingletonLogger(this);
  }

  async registerComponent(options) {
    const { namespace, implementation, descriptor, dependencies = [] } = options;
    
    if (!namespace || !implementation || !descriptor) {
      this.#logger.error("Missing required parameters: namespace, implementation, and descriptor");
      return null;
    }
    
    if (this.#runtimeComponents.has(namespace)) {
      this.#logger.error(`Component ${namespace} already registered in runtime registry`);
      return null;
    }
    
    this.#logger.log(`Processing component: ${namespace}`);
    
    // Store descriptor first
    this.#componentDescriptors.set(namespace, descriptor);
    descriptors.create(descriptor);
    
    // Create instance without using Entity constructor to avoid auto-registration
    const instance = new implementation();
    instance.namespace = namespace;
    instance.reinitializeLogger();

    // Register with engine
    const registered = coreEngine.registerRuntimeComponent(instance);
    
    if (!registered) {
      // Registration failed - clean up and return null
      descriptors.remove(namespace);
      this.#componentDescriptors.delete(namespace);
      this.#logger.error(`Failed to register component ${namespace} with CoreEngine`);
      return null;
    }
    
    // Track component
    this.#runtimeComponents.set(namespace, implementation);
    this.#componentInstances.set(namespace, instance);
    this.#componentDependencies.set(namespace, new Set(dependencies));
    
    this.#logger.log(`Component processed: ${namespace}`);
    
    return instance;
  }
  
  async unregisterComponent(namespace) {
    if (!this.#runtimeComponents.has(namespace)) {
      this.#logger.error(`Component ${namespace} not found in runtime registry`);
      return false;
    }
    
    this.#logger.log(`Unregistering runtime component: ${namespace}`);
    
    // Check for dependent components
    const dependents = this.#findDependents(namespace);
    if (dependents.length > 0) {
      this.#logger.error(`Cannot unregister ${namespace}: used by ${dependents.join(', ')}`);
      return false;
    }
    
    // Destroy all instances of this component
    this.#destroyComponentInstances(namespace);
    
    // Unregister from engine
    coreEngine.unregisterRuntimeComponent(namespace);
    
    // Remove descriptor
    descriptors.remove(namespace);
    
    // Clean up registry
    this.#runtimeComponents.delete(namespace);
    this.#componentInstances.delete(namespace);
    this.#componentDescriptors.delete(namespace);
    this.#componentDependencies.delete(namespace);
    
    this.#logger.log(`Successfully unregistered runtime component: ${namespace}`);
    return true;
  }
  
  has(namespace) {
    return this.#runtimeComponents.has(namespace);
  }
  
  getInstance(namespace) {
    return this.#componentInstances.get(namespace);
  }
  
  getDescriptor(namespace) {
    return this.#componentDescriptors.get(namespace);
  }
  
  getAllNamespaces() {
    return Array.from(this.#runtimeComponents.keys());
  }
  
  #findDependents(namespace) {
    const dependents = [];
    
    for (const [component, dependencies] of this.#componentDependencies) {
      if (dependencies.has(namespace)) {
        dependents.push(component);
      }
    }
    
    return dependents;
  }
  
  #destroyComponentInstances(namespace) {
    let instancesDestroyed = 0;
    
    // Find and destroy all node instances of this component
    const allNodes = nodes.getAll();
    for (const [uid, node] of allNodes.entries()) {
      if (node.namespace === namespace) {
        coreEngine.destroy(uid);
        instancesDestroyed++;
      }
    }
    
    this.#logger.log(`Destroyed ${instancesDestroyed} instances of component: ${namespace}`);
  }
}

const componentRegistry = new RuntimeComponentRegistry();
export { RuntimeComponentRegistry, componentRegistry };